import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import useResourceVoucher from '../../hooks/useResourceVoucher';
import useResourceVoucherProduct from '../../hooks/useResourceVoucherProduct';
import LockIconWrapper from '../components/LockIconWrapper';
import { useAuth } from '../../context/authContext';

function CreateResourceVoucherModal({ open, onClose, onSuccess }) {
  const { createResourceVoucher, isLoading, error } = useResourceVoucher();
  const { getAllResourceVoucherProducts } = useResourceVoucherProduct();
  const { checkRightAny } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [rvProducts, setRvProducts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    durationMS: 60 * 60000,
    personCount: 2,
    allowedPricingIds: [],
    expirationDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    creationComment: '',
    quantity: 1,
    resourceVoucherProductId: '',
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const items = await getAllResourceVoucherProducts();
        setRvProducts(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error('Error loading resource voucher products', e);
      }
    })();
  }, [open]);

  const selectedRvProduct = useMemo(
    () => rvProducts.find(p => p._id === formData.resourceVoucherProductId),
    [rvProducts, formData.resourceVoucherProductId]
  );

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleTabChange = (_, value) => {
    setActiveTab(value);
  };

  const isSubmitValid = useMemo(() => {
    const baseValid =
      !!formData.resourceVoucherProductId &&
      formData.personCount >= 1 &&
      !!formData.creationComment &&
      formData.creationComment.trim().length >= 10;
    if (!baseValid) return false;
    if (activeTab === 1) {
      const qty = parseInt(formData.quantity, 10);
      return !!qty && qty >= 1 && qty <= 10000;
    }
    return true;
  }, [formData, activeTab]);

  const validateForm = () => {
    const errs = {};
    if (!formData.creationComment || formData.creationComment.trim().length < 10) {
      errs.creationComment = 'Kommentar ist erforderlich (mind. 10 Zeichen)';
    }
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!isSubmitValid || !validateForm()) return;

    const quantity = activeTab === 1 ? parseInt(formData.quantity, 10) : 1;

    const payload = {
      resourceVoucherProductId: formData.resourceVoucherProductId,
      expirationDate: formData.expirationDate,
      creationComment: formData.creationComment,
      quantity,
    };

    const displayInfo = {
      Typ: activeTab === 1 ? 'Mehrere Produktgutscheine erstellen' : 'Produktgutschein erstellen',
      Anzahl: quantity.toString(),
      RVProdukt: selectedRvProduct?.name || '-',
      Dauer: selectedRvProduct?.durationMS
        ? `${Math.round(selectedRvProduct.durationMS / 60000)} Min`
        : '-',
      Personen:
        selectedRvProduct?.personCount != null ? String(selectedRvProduct.personCount) : '-',
      Ablaufdatum: formData.expirationDate,
    };

    const toastMessage = `Die Erstellungsanfrage für ${quantity} Produktgutschein${
      quantity > 1 ? 'e' : ''
    } wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;

    const onStored = res => {
      if (res) {
        onSuccess?.(res);
        onClose();
      }
    };

    const result = await createResourceVoucher(payload, toastMessage, onStored, displayInfo);
    if (result) {
      onSuccess?.(result);
      onClose();
    }
  };

  const handleClose = () => {
    setActiveTab(0);
    setFormData({
      durationMS: 60 * 60000,
      personCount: 2,
      allowedPricingIds: [],
      expirationDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      creationComment: '',
      quantity: 1,
      resourceVoucherProductId: '',
    });
    setValidationErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby='create-resource-voucher-modal'>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 640,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          borderRadius: '10px',
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h6'>Produktgutschein erstellen</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label='Einfacher Gutschein' />
          {checkRightAny('voucher:special:batch') && (
            <Tab
              label={
                <LockIconWrapper right='voucher:special:batch'>Mehrere Gutscheine</LockIconWrapper>
              }
            />
          )}
        </Tabs>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {'Ein Fehler ist aufgetreten'}
          </Alert>
        )}

        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Gutscheinprodukt</InputLabel>
            <Select
              label='Gutscheinprodukt'
              value={formData.resourceVoucherProductId}
              onChange={e => handleChange('resourceVoucherProductId', e.target.value)}
            >
              {rvProducts.map(p => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label='Ablaufdatum'
            type='date'
            value={formData.expirationDate}
            onChange={e => handleChange('expirationDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {activeTab === 1 && (
            <TextField
              label='Anzahl'
              type='number'
              value={formData.quantity}
              onChange={e => handleChange('quantity', e.target.value)}
              inputProps={{ min: 1, max: 10000 }}
              fullWidth
            />
          )}

          <TextField
            label='Kommentar'
            multiline
            rows={3}
            value={formData.creationComment}
            onChange={e => handleChange('creationComment', e.target.value)}
            error={!!validationErrors.creationComment}
            helperText={validationErrors.creationComment}
            required
          />

          <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
            <Button variant='outlined' onClick={handleClose} fullWidth>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isLoading || !isSubmitValid}
              fullWidth
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? (
                'Wird erstellt...'
              ) : activeTab === 0 ? (
                <LockIconWrapper right='voucher:create'>Produktgutschein erstellen</LockIconWrapper>
              ) : (
                <LockIconWrapper right='voucher:special:batch'>
                  {formData.quantity} Produktgutscheine erstellen
                </LockIconWrapper>
              )}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}

export default CreateResourceVoucherModal;
